/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { type FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import DeleteIcon from '@material-ui/icons/Delete';
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined';
import CloudUploadOutlinedIcon from '@material-ui/icons/CloudUploadOutlined';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import { makeStyles } from '@material-ui/core/styles';

export interface ProtoFile {
  filename: string;
  content: string;
}

const FILENAME_PATTERN = /^[a-z][a-z0-9_]*\.proto$/;
const MAX_FILE_SIZE = 1024 * 1024; // 1 MB
const MAX_FILE_COUNT = 50;

export function normalizeFilename(name: string): string {
  const base = name.replace(/\.proto$/, '');
  const normalized = base
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[-\s]+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
  return `${normalized}.proto`;
}

const useStyles = makeStyles(theme => ({
  dropZone: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 2,
    padding: theme.spacing(4),
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border-color 0.2s, background-color 0.2s',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
  dropZoneActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
  icon: {
    fontSize: 40,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  clickLabel: {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  fileList: {
    marginTop: theme.spacing(2),
    padding: 0,
  },
  fileItem: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
    paddingRight: theme.spacing(6),
  },
  fileIcon: {
    minWidth: 36,
    color: theme.palette.text.secondary,
  },
  warnings: {
    marginTop: theme.spacing(1),
    color: theme.palette.warning.main,
  },
}));

export const ProtoFileUpload = ({
  onChange,
  formData = [],
  rawErrors,
}: FieldExtensionComponentProps<ProtoFile[]>) => {
  const classes = useStyles();
  const [isDragging, setIsDragging] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);
  const filesRef = useRef<ProtoFile[]>(formData);
  const lineCountCache = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    filesRef.current = formData;
  }, [formData]);

  const getLineCount = useCallback((file: ProtoFile): number => {
    const cached = lineCountCache.current.get(file.filename);
    if (cached !== undefined) return cached;
    const count = file.content.split('\n').length;
    lineCountCache.current.set(file.filename, count);
    return count;
  }, []);

  const handleFiles = useCallback(
    async (fileList: FileList) => {
      const newWarnings: string[] = [];
      const filesToRead: { file: File; filename: string }[] = [];

      const currentCount = filesRef.current.length;
      const remainingCapacity = MAX_FILE_COUNT - currentCount;
      if (remainingCapacity <= 0) {
        newWarnings.push(`File limit reached (${MAX_FILE_COUNT} max)`);
        setWarnings(newWarnings);
        return;
      }

      for (const file of Array.from(fileList)) {
        if (filesToRead.length >= remainingCapacity) {
          newWarnings.push(
            `${file.name} — file limit reached (${MAX_FILE_COUNT} max), skipped`,
          );
          continue;
        }
        if (!file.name.endsWith('.proto')) {
          newWarnings.push(`${file.name} — not a .proto file, skipped`);
          continue;
        }
        if (file.size > MAX_FILE_SIZE) {
          newWarnings.push(`${file.name} — exceeds 1 MB limit, skipped`);
          continue;
        }
        if (file.size === 0) {
          newWarnings.push(`${file.name} — empty file, skipped`);
          continue;
        }

        const filename = normalizeFilename(file.name);

        if (!FILENAME_PATTERN.test(filename)) {
          newWarnings.push(
            `${file.name} — normalized to "${filename}" which is invalid, skipped`,
          );
          continue;
        }

        filesToRead.push({ file, filename });
      }

      const incoming: ProtoFile[] = [];
      for (const { file, filename } of filesToRead) {
        try {
          const content = await file.text();
          if (content.trim().length === 0) {
            newWarnings.push(`${filename} — file is empty or whitespace-only, skipped`);
            continue;
          }
          incoming.push({ filename, content });
        } catch {
          newWarnings.push(`${filename} — failed to read file, skipped`);
        }
      }

      if (incoming.length > 0 || newWarnings.length > 0) {
        const current = filesRef.current;
        const existingNames = new Set(current.map(f => f.filename));
        const accepted: ProtoFile[] = [];
        let total = current.length;

        for (const proto of incoming) {
          if (total >= MAX_FILE_COUNT) {
            newWarnings.push(
              `${proto.filename} — file limit reached (${MAX_FILE_COUNT} max)`,
            );
            continue;
          }
          if (existingNames.has(proto.filename)) {
            newWarnings.push(`${proto.filename} — already in list, skipped`);
            continue;
          }
          existingNames.add(proto.filename);
          lineCountCache.current.set(
            proto.filename,
            proto.content.split('\n').length,
          );
          accepted.push(proto);
          total += 1;
        }

        if (accepted.length > 0) {
          const updated = [...current, ...accepted];
          filesRef.current = updated;
          onChange(updated);
        }
      }

      setWarnings(newWarnings);
    },
    [onChange],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragging(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDelete = useCallback(
    (filename: string) => {
      lineCountCache.current.delete(filename);
      const updated = filesRef.current.filter(f => f.filename !== filename);
      filesRef.current = updated;
      onChange(updated);
    },
    [onChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        inputRef.current?.click();
      }
    },
    [],
  );

  return (
    <Box>
      <Box
        className={`${classes.dropZone} ${isDragging ? classes.dropZoneActive : ''}`}
        role="button"
        tabIndex={0}
        aria-label="Upload .proto files — click or drag and drop"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        onKeyDown={handleKeyDown}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".proto"
          multiple
          style={{ display: 'none' }}
          onChange={e => {
            if (e.target.files) handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <CloudUploadOutlinedIcon className={classes.icon} />
        <Typography variant="body1">
          <span className={classes.clickLabel}>Click to upload</span> or drag
          and drop
        </Typography>
        <Typography variant="body2" color="textSecondary">
          .proto files only (max 1 MB each, up to {MAX_FILE_COUNT} files)
        </Typography>
      </Box>

      {warnings.length > 0 && (
        <Box className={classes.warnings}>
          {warnings.map((w, i) => (
            <Typography key={i} variant="body2">
              {w}
            </Typography>
          ))}
        </Box>
      )}

      {formData.length > 0 && (
        <List className={classes.fileList}>
          {formData.map(file => (
            <ListItem key={file.filename} className={classes.fileItem}>
              <ListItemIcon className={classes.fileIcon}>
                <InsertDriveFileOutlinedIcon />
              </ListItemIcon>
              <ListItemText
                primary={file.filename}
                secondary={`${getLineCount(file)} lines`}
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => handleDelete(file.filename)}
                  aria-label={`Remove ${file.filename}`}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      )}

      {rawErrors && rawErrors.length > 0 && (
        <Typography color="error" variant="body2">
          {rawErrors.join(', ')}
        </Typography>
      )}
    </Box>
  );
};

export const protoFileUploadValidation = (
  value: ProtoFile[],
  validation: { addError: (message: string) => void },
) => {
  if (!value || !Array.isArray(value)) return;
  for (const file of value) {
    if (!FILENAME_PATTERN.test(file.filename)) {
      validation.addError(
        `Invalid filename "${file.filename}" — must be lowercase alphanumeric with underscores, ending in .proto`,
      );
    }
    if (!file.content || file.content.trim().length === 0) {
      validation.addError(`File "${file.filename}" has no content`);
    }
  }
};
